import {
  GET_CONVERSATIONS,
  GET_MESSAGES,
  SEND_MESSAGE,
  SET_ACTIVE_CONVERSATION,
  UPDATE_ACTIVE_CONVERSATION,
  MARK_AS_READ,
  GET_UNREAD_COUNT,
  DELETE_MESSAGE,
  DELETE_CONVERSATION,
  EDIT_MESSAGE,
  REACT_TO_MESSAGE,
  SEARCH_MESSAGES,
  CLEAR_SEARCH,
  PIN_MESSAGE,
  MESSAGES_LOADING,
  CLEAR_MESSAGES,
  GET_AVAILABLE_USERS
} from '../actions/messageActions';

const initialState = {
  conversations: [],
  messages: [],
  messagesCache: {},      // { "type/id": [...messages] }
  activeConversation: null,
  unreadCount: 0,
  loading: false,
  hasMore: false,
  currentPage: 1,
  totalPages: 1,
  availableUsers: [],
  searchResults: [],
  isSearching: false
};

export default function messageReducer(state = initialState, action) {
  switch (action.type) {
    case MESSAGES_LOADING:
      return {
        ...state,
        loading: true
      };

    case GET_CONVERSATIONS:
      return {
        ...state,
        conversations: action.payload,
        loading: false
      };

    case GET_MESSAGES: {
      const newMessages = action.payload.append
        ? [...state.messages, ...action.payload.messages]
        : action.payload.messages;
      const cacheKey = action.payload.cacheKey;
      return {
        ...state,
        messages: newMessages,
        messagesCache: cacheKey
          ? { ...state.messagesCache, [cacheKey]: newMessages }
          : state.messagesCache,
        hasMore: action.payload.hasMore,
        currentPage: action.payload.currentPage,
        totalPages: action.payload.totalPages,
        loading: false
      };
    }

    case SEND_MESSAGE: {
      const newMessages = [...state.messages, action.payload];
      const key = state.activeConversation
        ? `${state.activeConversation.type}/${state.activeConversation.id}`
        : null;
      return {
        ...state,
        messages: newMessages,
        messagesCache: key
          ? { ...state.messagesCache, [key]: newMessages }
          : state.messagesCache,
        loading: false
      };
    }

    case SET_ACTIVE_CONVERSATION: {
      if (!action.payload) {
        return { ...state, activeConversation: null, messages: [], currentPage: 1 };
      }
      const cacheKey = `${action.payload.type}/${action.payload.id}`;
      const cached = state.messagesCache[cacheKey] || [];
      return {
        ...state,
        activeConversation: action.payload,
        messages: cached,
        loading: cached.length === 0, // show spinner only if no cached messages
        currentPage: 1,
        searchResults: [],
        isSearching: false
      };
    }

    case UPDATE_ACTIVE_CONVERSATION:
      return {
        ...state,
        activeConversation: action.payload
        // Keep existing messages and pagination state
      };

    case MARK_AS_READ:
      return {
        ...state,
        messages: state.messages.map(message =>
          message._id === action.payload
            ? { ...message, isRead: true }
            : message
        )
      };

    case EDIT_MESSAGE: {
      const edited = action.payload;
      const updatedMessages = state.messages.map(m =>
        m._id === edited._id ? { ...m, content: edited.content, editHistory: edited.editHistory } : m
      );
      const cacheKey = state.activeConversation
        ? `${state.activeConversation.type}/${state.activeConversation.id}`
        : null;
      return {
        ...state,
        messages: updatedMessages,
        messagesCache: cacheKey
          ? { ...state.messagesCache, [cacheKey]: updatedMessages }
          : state.messagesCache
      };
    }

    case REACT_TO_MESSAGE: {
      const { messageId, reactions } = action.payload;
      const updatedMessages = state.messages.map(m =>
        m._id === messageId ? { ...m, reactions } : m
      );
      const cacheKey = state.activeConversation
        ? `${state.activeConversation.type}/${state.activeConversation.id}`
        : null;
      return {
        ...state,
        messages: updatedMessages,
        messagesCache: cacheKey
          ? { ...state.messagesCache, [cacheKey]: updatedMessages }
          : state.messagesCache
      };
    }

    case DELETE_MESSAGE: {
      const newMessages = state.messages.filter(m => m._id !== action.payload);
      const key = state.activeConversation
        ? `${state.activeConversation.type}/${state.activeConversation.id}`
        : null;
      return {
        ...state,
        messages: newMessages,
        messagesCache: key
          ? { ...state.messagesCache, [key]: newMessages }
          : state.messagesCache
      };
    }

    case DELETE_CONVERSATION:
      return {
        ...state,
        conversations: state.conversations.filter(conversation => conversation.id !== action.payload),
        // Clear active conversation and messages if the deleted conversation was active
        activeConversation: state.activeConversation?.id === action.payload ? null : state.activeConversation,
        messages: state.activeConversation?.id === action.payload ? [] : state.messages,
        currentPage: state.activeConversation?.id === action.payload ? 1 : state.currentPage,
        hasMore: state.activeConversation?.id === action.payload ? false : state.hasMore
      };

    case GET_UNREAD_COUNT:
      return {
        ...state,
        unreadCount: action.payload
      };

    case GET_AVAILABLE_USERS:
      return {
        ...state,
        availableUsers: action.payload
      };

    case PIN_MESSAGE: {
      const pinned = action.payload;
      const updatedMessages = state.messages.map(m =>
        m._id === pinned._id ? { ...m, isPinned: pinned.isPinned, pinnedBy: pinned.pinnedBy, pinnedAt: pinned.pinnedAt } : m
      );
      const cacheKey = state.activeConversation
        ? `${state.activeConversation.type}/${state.activeConversation.id}`
        : null;
      return {
        ...state,
        messages: updatedMessages,
        messagesCache: cacheKey
          ? { ...state.messagesCache, [cacheKey]: updatedMessages }
          : state.messagesCache
      };
    }

    case SEARCH_MESSAGES:
      return {
        ...state,
        searchResults: action.payload.messages,
        isSearching: true
      };

    case CLEAR_SEARCH:
      return {
        ...state,
        searchResults: [],
        isSearching: false
      };

    case CLEAR_MESSAGES:
      return {
        ...state,
        messages: [],
        activeConversation: null,
        currentPage: 1,
        hasMore: false
      };

    default:
      return state;
  }
}
